import {produce} from 'immer';
import {z} from 'zod';
import {SearchState} from '@/types/models/creator-crm/creatorCrm';
import {
  CreatorCrmStateDefinition,
  TableColumnConfig,
  ViewType,
} from '@/types/models/creator-crm/creatorCrmStore';
import {FiltersGroup, FiltersState} from '@/types/models/creator-crm/filter';
import {FilterIds, FiltersGroupId} from '@/types/models/creator-crm/filterId';
import {SortingState as StateSorting} from '@/types/models/creator-crm/sorting';
import mapToAppliedFilters from '@/views/creators/creator-crm/utils/mapToAppliedFilters';
import {
  applyAppliedFiltersToDraft,
  type AppliedFiltersGroups,
} from '@/stores/creator-crm/creatorCrmStore';

const SearchStateSchema = z.object({
  type: z.enum(['bio', 'handle', 'content', 'all']),
  chips: z.array(z.string()),
});

type PersistenceState = {
  viewType: ViewType;
  searchState?: SearchState;
  appliedFiltersGroups?: AppliedFiltersGroups;
  sorting?: StateSorting;
  selectedBrandId: string;
  tableColumns?: TableColumnConfig[];
  selectedSegmentId?: string | null;
  isCrmMode?: boolean;
};

export function mapCreatorCrmStateToPersistenceState(
  viewType: ViewType,
  filterGroups: Record<FiltersGroupId, FiltersGroup<FilterIds>>,
  sorting: StateSorting | undefined,
  selectedBrandId: string,
  tableColumns?: TableColumnConfig[],
  filtersState?: FiltersState,
  selectedSegmentId?: string | null,
  isCrmMode?: boolean
): PersistenceState {
  return {
    viewType,
    searchState: filtersState?.searchState,
    appliedFiltersGroups: mapToAppliedFilters(filterGroups),
    sorting,
    selectedBrandId,
    tableColumns,
    selectedSegmentId: selectedSegmentId ?? null,
    isCrmMode,
  };
}

export function mapPersistenceStateToCreatorCrmState(
  json: string,
  state: CreatorCrmStateDefinition
): CreatorCrmStateDefinition {
  const persistenceState: PersistenceState = JSON.parse(json);

  return produce(state, (draft) => {
    const parsed = persistenceState.searchState
      ? SearchStateSchema.safeParse(persistenceState.searchState)
      : null;
    const searchState = parsed?.success ? parsed.data : undefined;
    applyAppliedFiltersToDraft(
      draft,
      persistenceState.appliedFiltersGroups ?? {},
      searchState
    );
    draft.viewType = persistenceState.viewType;
    draft.content.sorting = persistenceState.sorting;
    draft.selectedBrandId = persistenceState.selectedBrandId;
    draft.selectedSegmentId = persistenceState.selectedSegmentId ?? null;
    if (persistenceState.tableColumns?.length) {
      draft.content.tableColumns = persistenceState.tableColumns;
    }
    if (persistenceState.isCrmMode !== undefined) {
      draft.isCrmMode = persistenceState.isCrmMode;
    }
  });
}
